import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RiEdit2Line, RiMailLine } from '@remixicon/react';
import { DefaultEditor } from 'react-simple-wysiwyg';
import Modal from '@components/ui/Modal';
import { Input } from '@components/ui/Input';
import { cn } from '@components/ui/utils';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { getAircraftModelName } from '@/features/tails/utils';
/**
 * QuoteCustomizeModal Component
 * Modal for customizing quote details before preview
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Object} props.settings - Current quote settings
 * @param {Function} props.onConfirm - Callback when user confirms customization
 */
const QuoteCustomizeModal = ({
  isOpen = false,
  onClose,
  settings = {},
  onConfirm,
  bidOptions = []
}) => {

  const { userProfile } = useAuthStore();

  const { data: organization } = useQuery({
    queryKey: ['organization', userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  const quoteItems = bidOptions?.map(item => ({
    operator: item.operator?.name,
    price: item.final_price || item.total_price,
    aircraft: `${getAircraftModelName(item.aircraft)}`
  })) || [];

  const quoteItemsHtml = (quoteItems || [])
    .map(item => `<li>
      <ul>
        <li>Aircraft: ${item.aircraft}</li>
        <li>Price: $${item.price}</li>
        <li>Operator: ${item.operator}</li>
      </ul>
    <p><br></p></li>`)
    .join('');
  
  // Quote details section with placeholders - shown in editor
  const quoteDetailsSection = `
  <h2><strong>Quote Details:</strong></h2>
  <ul>
    <li>Link: <a href="{{quote_link_address}}" target="_blank">{{quote_link}}</a></li>
  </ul>
  <p><br></p>
  <h2><strong>Quote Items:</strong></h2>
  <ul>
    ${quoteItemsHtml}
  </ul>
  `;

  // Default email message template with quote details included
  const getDefaultEmailMessage = () => {
    return `<p>Dear {{client_name}},</p><p><br></p>
  <p>Thank you for your interest in our private jet services. Please find your quote details below.</p><p><br></p>
  ${quoteDetailsSection}
  ${settings.notes ? 'Note: '+settings.notes+'</p><p><br></p><p>' : ''}
  <p>If you have any questions or would like to proceed with booking, please don't hesitate to contact us.</p>
  <p><br></p>
  <p>Best regards,<br>${organization?.name || ''}</p>`;
  };

  // Local state for form fields
  const [formData, setFormData] = useState({
    emailSubject: '',
    emailMessage: '',
  });

  // Initialize form data when modal opens or when organization loads
  useEffect(() => {
    if (isOpen && organization) {
      setFormData({
        emailSubject: `Trip (${settings.bidTitle})`,
        emailMessage: settings.emailMessage || getDefaultEmailMessage(),
      });
    }
  }, [isOpen, organization]);

  // Handle field change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle confirm - pass the email message as-is
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(formData);
    }
  };

  // Check if form is valid
  const isFormValid = formData.emailSubject?.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Customize Quote Email"
      size="md"
      closeOnOutsideClick={false}
    >
      <div className="p-6">
        <div className="space-y-5">

          {/* Email Subject */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <RiMailLine className="w-4 h-4 text-gray-500" />
              Email Subject *
            </label>
            <Input
              type="text"
              value={formData.emailSubject}
              onChange={(e) => handleChange('emailSubject', e.target.value)}
              placeholder="e.g., Your Flight Quote"
              maxLength={150}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Subject line for the email sent to the client
            </p>
          </div>

          {/* Email Message */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <RiEdit2Line className="w-4 h-4 text-gray-500" />
              Email Message
            </label>
            <DefaultEditor
              value={formData.emailMessage}
              onChange={(e) => handleChange('emailMessage', e.target.value)}
              style={{ minHeight: '250px' }}
            />
            <p className="mt-2 text-xs text-gray-500">
              The placeholders <span className="font-medium text-gray-600">{'{{client_name}}'}</span>, <span className="font-medium text-gray-600">{'{{quote_number}}'}</span> and <span className="font-medium text-gray-600">{'{{quote_link}}'}</span> will be replaced with actual values when sent.
            </p>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid}
            className={cn(
              'flex-1 px-4 py-2.5 bg-primary-500 text-white rounded-lg transition-colors font-medium',
              isFormValid ? 'hover:bg-primary-600' : 'opacity-50 cursor-not-allowed'
            )}
          >
            Continue to Send
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteCustomizeModal;
